/**
 * @typedef {import('vue').ComponentOptions<Vue>} VueComponent
 */

/** @type {VueComponent} */
import Vue from 'vue';
import { getSwiper, getSwiperModules } from '../utils';

export default {
    props: {
        allCompareSections: {
            type: Object,
        },
        allAssets: {
            type: Object,
        },
        contentSwitcher: {
            type: Array,
        },
    },

    data() {
        return {
            isMounted: false,
            allCombinedCompareSections: null,
            linkBuilder: null,
            areSpecsVisible: false,
            specsSelectedModelName: '',
            selectedSpecs: null,
            activeModelType: 'all',

            hideAllFilter: false,
            hidePinButton: false,
            allFilterTranslation: 'All',
            selectedModel: null,

            selectedConfigurationTitle: null,
            selectedConfiguration: null,
            selectedSpecsModelType: null,
            selectedSpecsModelSubline: null,
            selectedSpecsModelSublineAsset: null,
            selectedSpecsDisclaimer: null,
        };
    },

    components: {
        'cta-builder-component': () => import('./async-cta-builder-component'),
    },

    watch: {
        areSpecsVisible(newValue) {
            window.document.body.style.overflowY = newValue ? 'hidden' : 'auto';
            if (!newValue) return;
        },
    },

    created() {
        this.selectedModel = this.$parent.initialModel;

        EventBus.$on('model-version-changed', (versionHandle) => {
            this.selectedModel = versionHandle;
            this.handleChangeModel();
        });
    },

    async mounted() {
        this.handleChangeModel();
        this.isMounted = true;
        await this.initSwiper();
    },

    computed: {
        sortedCombinedCompareSections() {
            return this.allCombinedCompareSections.sort(
                (a, b) => b.isPinned - a.isPinned,
            );
        },
    },

    methods: {
        async initSwiper() {
            const { Swiper } = await getSwiper();
            const { Scrollbar, Navigation } = await getSwiperModules();

            const element = document.querySelector(
                '.new-compare-block__cars-container',
            );

            const isDesktop = window.screen.width >= 768;

            new Swiper(element, {
                slidesPerView: 'auto',
                spaceBetween: isDesktop ? 32 : 8,
                centeredSlides: false,
                loop: false,
                observer: true,
                observeParents: true,
                modules: [Scrollbar, Navigation],
                scrollbar: {
                    el: '.swiper-scrollbar-model-compare',
                    hide: false,
                    draggable: true,
                },
                uniqueNavElements: true,
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                },
            });
        },

        handleChangeModel() {
            const filteredCompareSections = Object.values(
                this.allCompareSections,
            ).filter(Boolean);

            const selectedCompareSection = filteredCompareSections?.find(
                (section) => section.modelSlug === this.selectedModel,
            );

            if (this.selectedModel && selectedCompareSection) {
                const sections = selectedCompareSection;
                this.allFilterTranslation = sections.allFilterTranslation;
                this.hideAllFilter = sections.hideAllFilter;
                this.hidePinButton = sections.hidePinButton;
            }

            this.allCombinedCompareSections = filteredCompareSections.flatMap(
                ({
                    compareBlock,
                    modelSlug,
                    allFilterTranslation,
                    hideAllFilter,
                    hidePinButton,
                }) => {
                    return compareBlock.carModels.map((carModel) => ({
                        carModel,
                        modelSlug,
                        isPinned: false,
                    }));
                },
            );
        },

        mergeSpecificationLists(specificationEvenList, specificationOddList) {
            const mergedList = {};

            // Get all keys from both objects and sort them numerically
            const allKeys = [
                ...Object.keys(specificationEvenList),
                ...Object.keys(specificationOddList),
            ]
                .map(Number)
                .sort((a, b) => a - b);

            // Merge the objects in order
            allKeys.forEach((key) => {
                if (specificationEvenList[key] !== undefined) {
                    mergedList[key] = specificationEvenList[key];
                } else if (specificationOddList[key] !== undefined) {
                    mergedList[key] = specificationOddList[key];
                }
            });
            return mergedList;
        },

        styleSelectedModelType(modelType) {
            return this.activeModelType === modelType
                ? 'new-compare-block__button--active'
                : '';
        },

        changeActiveModelType(modelType) {
            this.activeModelType = modelType;
        },

        togglePin(index) {
            this.allCombinedCompareSections[index].isPinned =
                !this.allCombinedCompareSections[index].isPinned;
        },

        showModelsByType(modelType) {
            if (modelType === 'all') {
                return this.sortedCombinedCompareSections;
            }

            return this.sortedCombinedCompareSections.filter(
                (compare) => compare.modelSlug === modelType,
            );
        },

        openSpecs(carModel, modelSlug) {
            const {
                name: modelName,
                type: modelType,
                subline: modelSubline,
                sublineAsset: modelSublineAsset,
            } = carModel;
            this.areSpecsVisible = true;

            const selectedSpecs = this.allCompareSections[modelSlug];
            this.selectedSpecs = selectedSpecs;
            this.selectedConfigurationTitle =
                selectedSpecs.configValues[0].title;
            this.selectedConfiguration = this.getSelectedConfiguration(
                selectedSpecs.configValues[0].title,
            );
            this.specsSelectedModelName = modelName;
            this.selectedSpecsModelType = modelType;
            this.selectedSpecsModelSubline = modelSubline;
            this.selectedSpecsModelSublineAsset = modelSublineAsset?.url;
        },

        closeSpecs() {
            this.areSpecsVisible = false;
        },

        selectConfiguration(configuration) {
            this.selectedConfigurationTitle = configuration;
            this.selectedConfiguration =
                this.getSelectedConfiguration(configuration);
        },

        getSelectedConfiguration(configuration) {
            const selectedConfiguration = this.selectedSpecs.configValues.find(
                (configValue) => configValue.title === configuration,
            );
            if (!selectedConfiguration) return null;
            this.selectedSpecsDisclaimer = selectedConfiguration.disclaimer;

            const { editions, values } = selectedConfiguration;

            const filteredEditions = editions.filter(
                (edition) => !edition.isDisabled,
            );

            return values.map((value) => ({
                tableTitle: [
                    { title: value.title },
                    ...filteredEditions.map((edition) => ({
                        title: edition.name,
                        subtitle: edition.subtitle,
                    })),
                ],
                tableData: value.values,
            }));
        },

        isSelectedConfiguration(configuration) {
            return this.selectedConfigurationTitle === configuration
                ? 'new-model-specs__sidebar__selector--active'
                : '';
        },

        getSpecificModelTypeLength(modelSlug) {
            if (!this.allCompareSections[modelSlug]) return 0;
            return this.allCompareSections[modelSlug].compareBlock.carModels
                .length;
        },

        checkItemsLength(selectedConfiguration) {
            return selectedConfiguration[0].tableTitle.length <= 3
                ? ''
                : 'new-model-specs__table-container--horizontal-scroll';
        },
    },
};
